import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AppSidebar } from './AppSidebar';
import { AppHeader } from './AppHeader';
import { AIFillerPanel } from '../AIFillerPanel';
import { OnboardingTour } from '../Onboarding';
import { GlobalSearch } from '../Search/GlobalSearch';
import { DemoBanner } from '../Demo/DemoBanner';
import { AnalysisRunsDock } from '../Projects/analysis/AnalysisRunsDock';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { useIsSuperAdmin, useAuthStore, useImpersonation } from '../../stores/authStore';
import { useLayoutStore, useSidebarCollapsed } from '../../stores/layoutStore';
import { useTourStore } from '../../stores/tourStore';
import { useTourGuard } from '../../config/onboardingTours';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { useNotificationBridge } from '../../hooks/useNotificationBridge';
import { useDemoMode } from '../../hooks/useDemoMode';
import { useApplyBranding } from '../../hooks/useApplyBranding';
import { useLoadOrgUiSettings } from '../../hooks/useOrgModules';
import { useApplyAppNameTitle } from '../../hooks/useAppName';
import { cn } from '../../lib/utils';

const HOME_PATHS = new Set(['/', '/welcome', '/dashboard', '/projets', '/portefeuille']);

interface AppLayoutProps {
  children?: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { activeTourId, skipTour, requestResume } = useTourGuard();
  const user = useAuthStore((s) => s.user);
  // A super admin's org switch remounts every routed page so its data reloads for the new org.
  const orgKey = user?.organization_id ?? 'no-org';
  const startTour = useTourStore((s) => s.startTour);
  const { t } = useTranslation();
  const isSuperAdmin = useIsSuperAdmin();
  const { isFullscreen, exitFullscreen, setSidebarForced, isChromeHidden } = useLayoutStore();
  const isSidebarCollapsed = useSidebarCollapsed();
  const { items: breadcrumbs, orgName, isPlatformOrg } = useBreadcrumbs();
  const { isImpersonating } = useImpersonation();
  // The platform org is empty: on a home page, point the super admin at the switcher.
  const showChooseOrgHint =
    isSuperAdmin && !isImpersonating && isPlatformOrg && HOME_PATHS.has(location.pathname);
  const [searchOpen, setSearchOpen] = useState(false);
  const isChatPage = location.pathname === '/chat';
  const isGenieArtifactPage = location.pathname.startsWith('/genie-artifact');
  const isFullWidthPage = isChatPage || isGenieArtifactPage;
  const { isDemo } = useDemoMode();
  useNotificationBridge();
  useApplyBranding();
  useLoadOrgUiSettings();
  useApplyAppNameTitle();

  useEffect(() => {
    if (!isFullWidthPage) return;
    setSidebarForced(true);
    return () => setSidebarForced(false);
  }, [isFullWidthPage, setSidebarForced]);

  // Handle fullscreen mode with ESC key + global Cmd+K search shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        exitFullscreen();
      }
      // Cmd+K or Ctrl+K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, exitFullscreen]);

  // First-run onboarding: auto-launch the welcome tour for new users on each login
  // (session-scoped) until they dismiss it with "don't show again" (persisted per-user).
  useEffect(() => {
    if (location.pathname === '/no-organization' || isFullscreen) return;
    if (!user || activeTourId) return;
    if (user.preferences?.onboarding_completed) return;
    if (sessionStorage.getItem('welcome-autostart-done')) return;
    sessionStorage.setItem('welcome-autostart-done', 'true');
    startTour('welcome');
  }, [user, activeTourId, isFullscreen, location.pathname, startTour]);

  // Check if we should hide the layout (sidebar & header) for specific pages
  const shouldHideLayout = location.pathname === '/no-organization' || isChromeHidden;

  if (shouldHideLayout) {
    return (
      <div className="flex h-screen bg-background">
        {children ? children : <Outlet />}
      </div>
    );
  }

  // Fullscreen hides the chrome but keeps the same tree: a separate branch would remount the
  // routed page and wipe its state (selected tab, unsaved edits).
  return (
    <div className="flex h-screen bg-background flex-col">
      {/* Demo banner — shown only for demo users, above everything else */}
      {isDemo && !isFullscreen && <DemoBanner />}

      {/* Header - Full width */}
      {!isFullscreen && (
        <AppHeader breadcrumbs={breadcrumbs} orgName={orgName} onSearchOpen={() => setSearchOpen(true)} isSuperAdmin={isSuperAdmin} />
      )}

      {/* Content with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar */}
        {!isFullscreen && <AppSidebar />}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {/* Page Content — chat uses full width/height without outer padding */}
          <div
            className={cn(
              'flex-1 min-h-0 transition-all duration-300 flex flex-col',
              isFullWidthPage ? 'overflow-hidden p-0' : 'overflow-y-auto py-6 px-3 sm:px-4',
              isSidebarCollapsed && 'ml-0'
            )}
          >
            <div
              className={cn(
                isFullWidthPage ? 'h-full min-h-0 flex flex-col flex-1 w-full' : 'max-w-9xl mx-auto w-full'
              )}
            >
              {showChooseOrgHint && (
                <div
                  className={cn(
                    'flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-4 py-2 text-sm text-muted-foreground',
                    isFullWidthPage ? 'm-3' : 'mb-4'
                  )}
                  role="status"
                >
                  <Building2 className="h-4 w-4 flex-shrink-0" />
                  {t('orgSwitcher.chooseOrgHint')}
                </div>
              )}
              {children ? children : <Outlet key={orgKey} />}
            </div>
          </div>
        </div>
      </div>

      {/* Overlays stay hidden in fullscreen, where the section covers the whole screen. */}
      {!isFullscreen && (
        <>
          {/* GenieFiller - Panel only (no FAB, opening handled by GenieMenu) */}
          <AIFillerPanel />

          {/* Global Search */}
          <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

          {/* Project analyses in flight (bottom-right); also drives their polling */}
          <AnalysisRunsDock />

          {/* Tour mode floating label - bottom-left, visible when a tour is active */}
          {activeTourId && (
            <div
              className="fixed bottom-4 left-4 z-[10050] flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 shadow-lg"
              role="status"
              aria-live="polite"
            >
              <span className="text-sm font-medium text-foreground">
                {t('onboarding.tourModeLabel')}
              </span>
              <button
                type="button"
                onClick={() => requestResume()}
                className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t('onboarding.tourModeResume')}
              </button>
              <button
                type="button"
                onClick={() => skipTour()}
                className="rounded-md bg-muted px-2 py-1 text-xs font-medium text-foreground hover:bg-muted/80 transition-colors"
              >
                {t('onboarding.tourModeCancel')}
              </button>
            </div>
          )}

          {/* Onboarding Tour - shown when a tour is active (state in tourStore) */}
          {activeTourId && (
            <OnboardingTour tourId={activeTourId} />
          )}
        </>
      )}
    </div>
  );
};
